import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  useTheme
} from '@mui/material';
import { CheckCircle, Close, Error } from '@mui/icons-material';

// Styled Dialog for consistent styling
const StyledDialog = styled(Dialog)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    '& .MuiDialog-paper': {
      borderRadius: '16px',
      boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2)',
      background: isDarkMode 
        ? 'linear-gradient(135deg, rgba(30, 30, 30, 0.95), rgba(42, 42, 42, 0.95))'
        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(240, 245, 255, 0.95))',
      backdropFilter: 'blur(10px)',
      overflow: 'hidden',
      [theme.breakpoints.down('sm')]: {
        margin: '16px',
        width: 'calc(100% - 32px)',
        maxWidth: '100%',
      }
    }
  };
});

const DialogHeader = styled(DialogTitle)(({ theme }) => ({
  color: 'white',
  padding: theme.spacing(2, 3),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const SuccessButton = styled(Button)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    background: isDarkMode 
      ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
      : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white',
    borderRadius: '12px',
    padding: '10px 20px',
    fontWeight: 600,
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
    '&:hover': {
      background: isDarkMode 
        ? 'linear-gradient(135deg, #047857 0%, #065f46 100%)'
        : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(16, 185, 129, 0.3)',
    }
  };
});

const ErrorButton = styled(Button)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    background: isDarkMode 
      ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
      : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    color: 'white',
    borderRadius: '12px',
    padding: '10px 20px',
    fontWeight: 600,
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)',
    '&:hover': {
      background: isDarkMode 
        ? 'linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)'
        : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(239, 68, 68, 0.3)',
    }
  };
});

/**
 * A reusable status dialog component for success and error states
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to call when dialog is closed
 * @param {string} props.message - Message to display
 * @param {string} [props.title="Success"] - Dialog title
 * @param {string} [props.buttonText="Continue"] - Text for the action button
 * @param {string} [props.type="success"] - Dialog type: "success" or "error"
 * @returns {JSX.Element} Status dialog component
 */
const StatusDialog = ({ 
  open, 
  onClose, 
  message, 
  title = "Success", 
  buttonText = "Continue",
  type = "success"
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  const isSuccess = type === "success";
  const color = isSuccess ? '#10b981' : '#ef4444';
  const bgColor = isSuccess 
    ? (isDarkMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.1)')
    : (isDarkMode ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.1)');
  const Icon = isSuccess ? CheckCircle : Error;
  const ActionButton = isSuccess ? SuccessButton : ErrorButton;
  
  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogHeader sx={{ 
        background: type === 'success' 
          ? (isDarkMode 
              ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
              : 'linear-gradient(135deg, #10b981 0%, #059669 100%)')
          : (isDarkMode 
              ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
              : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)')
      }}>       
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon fontSize="small" />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, fontSize: '1.1rem', color: 'white' }}>
            {title}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogHeader>
      
      <DialogContent sx={{ 
        pt: 4, 
        pb: 3,
        backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff'
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          py: 2
        }}>
          <Box sx={{ 
            mb: 3,
            p: 2,
            backgroundColor: bgColor,
            borderRadius: '50%',
            border: isDarkMode ? `1px solid ${color}30` : undefined
          }}>
            <Icon sx={{ fontSize: 60, color: color }} />
          </Box>
          <Typography 
            variant="h6" 
            gutterBottom 
            align="center" 
            sx={{ 
              fontWeight: 600, 
              color: color 
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="body1" 
            align="center"
            sx={{
              color: isDarkMode ? '#ffffff' : 'inherit'
            }}
          >
            {message}
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 3, 
        pt: 0,
        backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
        borderTop: `1px solid ${isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.12)'}`
      }}>
        <ActionButton onClick={onClose} fullWidth>
          {buttonText}
        </ActionButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default StatusDialog;